import { useState, useEffect } from 'react';
import {
  Form,
  Select,
  Row,
  Col,
  DatePicker,
  Button,
  ConfigProvider,
  Table,
  message,
  Input,
} from 'antd';
import dayjs from 'dayjs';
import {
  getFileTypeApi,
  getFileVersionApi,
  getFileListApi,
  downoadAllApi,
  getTransferTaskStatistics,
  getTransferTableData,
  getScenarioTypeApi,
} from '@/services/slaTransform';
import { getStorageFileUrl } from '@/services/initial';

import PtxEmpty from '@/components/ptx-empty';
import { EmptyFileColor } from 'patternx-svg';
import './index.less';

const { RangePicker } = DatePicker;
const TRANSFORM_ENUM: any = {
  // transfering: '转换中',
  // transfered: '转换完成',
  // fail: '转换失败',
  // waiting: '等待转换',
  1: '完成',
  2: '失败',
};

const SlaTransform: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [fileType, setFileType] = useState([]);
  const [fileVersion, setFileVersion] = useState([]);
  const [scenarioType, setScenarioType] = useState([]);
  const [fileName, setFileName] = useState([
    {
      value: '',
      label: '',
    },
  ]);
  const [fileInfo, setFileInfo] = useState({
    failedCnt: 0,
    failedRatio: 0,
    gltfAvgCost: 0,
    gltfMaxCost: 0,
    gltfMinCost: 0,
    totalAvgCost: 0,
    totalMaxCost: 0,
    totalMinCost: 0,
    glbAvgCost: 0,
    glbMaxCost: 0,
    glbMinCost: 0,
    successCnt: 0,
    successScopeMap: null as any,
  });
  const [data, setData] = useState([]);
  const [params, setParams] = useState({});

  const [page, setPage] = useState<number>(1); // 当前页码
  const [size, setSize] = useState<number>(10); // 每页显示数据数量
  const [total, setTotal] = useState<number>(0); // 总共数据量

  // table内部-下载文件
  const downloadFile = async (fileId: string) => {
    console.log('downloadFile', fileId);
    message.info('请稍等，正在导出文件');
    const res = await getStorageFileUrl(fileId);
    console.log(res);
    if (res?.success) {
      const a = document.createElement('a');
      a.href = res?.data?.details;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const columns: any = [
    {
      title: '序号',
      dataIndex: 'rowNo',
      width: 80,
      render: (text: string, record: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: '开始时间',
      dataIndex: 'startTime',
      width: 200,
    },
    {
      title: '场景',
      dataIndex: 'scenario',
      width: 100,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: '转换结果',
      dataIndex: 'transferStatus',
      width: 100,
      render: (text: string) => <span>{TRANSFORM_ENUM[text] || '-'}</span>,
    },
    {
      title: '全部完成时间',
      dataIndex: 'endTime',
      width: 200,
    },
    {
      title: 'gltf耗时',
      dataIndex: 'taskGltfCost',
      width: 100,
    },
    {
      title: 'glb耗时',
      dataIndex: 'taskGlbCost',
      width: 100,
    },
    {
      title: '全部耗时',
      dataIndex: 'taskTotalCost',
      width: 150,
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      width: 100,
    },
    {
      title: '文件名',
      dataIndex: 'fileName',
      width: 150,
    },
    {
      title: '文件大小',
      dataIndex: 'fileSize',
      width: 100,
      render: (text: number) => <span>{(text / 1024 / 1024).toFixed(2)}M</span>,
    },
    {
      title: '文件版本',
      dataIndex: 'fileVersion',
      width: 100,
    },
    {
      title: 'storageFileId',
      dataIndex: 'storageFileId',
      width: 120,
    },
    {
      title: '三角面片数',
      dataIndex: 'triangleFacesCnt',
      key: 'triangleFacesCnt',
      width: 120,
      render: (text: any) => <div>{text || '-'}</div>,
    },
    {
      title: '构件数',
      dataIndex: 'elementCnt',
      key: 'elementCnt',
      width: 100,
      render: (text: any) => <div>{text || '-'}</div>,
    },
    {
      title: '图纸数',
      dataIndex: 'sheetCnt',
      key: 'sheetCnt',
      width: 100,
      render: (text: any) => <div>{text || '-'}</div>,
    },
    {
      title: '操作',
      dataIndex: 'operate',
      fixed: 'right',
      width: 100,
      render: (_: any, record: any) => {
        return (
          <div className="action-box">
            <span onClick={() => downloadFile(record.storageFileId)}>下载</span>
          </div>
        );
      },
    },
  ];

  // fileType
  const getFileType = async () => {
    const res = await getFileTypeApi();
    if (res?.success) {
      const newFileType = res?.data?.details.map((item: any) => {
        return {
          label: item,
          value: item,
        };
      });
      setFileType(newFileType);
    } else {
      message.error('获取文件类型失败');
    }
  };

  // fileVersion
  const getFileVersion = async () => {
    const res = await getFileVersionApi();
    if (res?.success) {
      const newFileVersion = res?.data?.details.map((item: any) => {
        return {
          label: item,
          value: item,
        };
      });
      setFileVersion(newFileVersion);
    } else {
      message.error('获取文件版本失败');
    }
  };

  // scenarioType
  const getScenarioType = async () => {
    const res = await getScenarioTypeApi();
    if (res?.success) {
      const types = res?.data?.details.map((item: any) => {
        return {
          label: item,
          value: item,
        };
      });
      setScenarioType(types);
    }
  };

  // 获取sla转换table数据
  const getFileList = async (params: any) => {
    setIsLoading(true);
    const res = await getTransferTableData({ ...params, pageIndex: page, pageSize: size });
    if (res?.success) {
      console.log('getSlaData', res, page, size);
      if (res.data.current > res.data.pages) {
        // 当当前页数超过最大数据的页数时，重新请求数据
        const resOnceMore = await getTransferTableData({
          ...params,
          pageIndex: res.data.pages,
          pageSize: size,
        });
        const datas = resOnceMore?.data?.records;
        setData(datas);
        setTotal(resOnceMore?.data.total || 0);
        setPage(resOnceMore?.data.current || 1);
        setSize(resOnceMore?.data.size || 10);
      } else {
        setTotal(res.data.total);
        setPage(res.data.current);
        setSize(res.data.size);
        setData(res.data.records);
      }
    } else {
      setData([]);
      message.error('获取文件列表失败');
    }
    setIsLoading(false);
  };

  // 获取sla统计数据信息
  const getSlaTransformStatistics = async (params: any) => {
    const res = await getTransferTaskStatistics({ ...params });
    console.log('getSlaTransformStatistics', res);
    if (res?.success) {
      const details = res?.data?.details;
      setFileInfo({
        failedCnt: details?.failedCnt,
        failedRatio: details?.failedRatio,
        gltfAvgCost: details?.gltfAvgCost,
        gltfMaxCost: details?.gltfMaxCost,
        gltfMinCost: details?.gltfMinCost,
        totalAvgCost: details?.totalAvgCost,
        totalMaxCost: details?.totalMaxCost,
        totalMinCost: details?.totalMinCost,
        glbAvgCost: details?.glbAvgCost || 0,
        glbMaxCost: details?.glbMaxCost || 0,
        glbMinCost: details?.glbMinCost || 0,
        successCnt: details?.successCnt || 0,
        successScopeMap: details?.successScopeMap,
      });
    }
  };

  // 初始化
  const init = () => {
    getFileType();
    getFileVersion();
    getFileList({}); // 获取sla转换table数据
    getSlaTransformStatistics({}); // 获取sla统计数据信息
    getScenarioType();
  };

  const downoadAll = async () => {
    message.info('请稍等，正在导出文件');
    const res = await downoadAllApi(params);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(new Blob([res!.data]));
    link.setAttribute('download', '转换SLA.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderEmpty = () => {
    return <PtxEmpty isEmpty={true} describe="暂无数据" icon={<EmptyFileColor />} />;
  };

  // 搜索-文件名称
  const handleFileNameInput = (e: any) => {
    const value = e.target.value;
    if (value === '') {
      form.setFieldValue('fileName', undefined);
    } else {
      form.setFieldValue('fileName', value);
    }
  };

  // 确认搜索
  const onFinish = (values: any) => {
    const { time } = values;
    if (time) {
      values.startTime = dayjs(time[0]).format('YYYY-MM-DD HH:mm:ss');
      values.endTime = dayjs(time[1]).format('YYYY-MM-DD HH:mm:ss');
    }
    delete values.time;
    setParams(values);
    console.log('Success:', values);
    getFileList(values);
    getSlaTransformStatistics(values);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    const value = form.getFieldsValue(true);
    if (value.time) {
      const paramData = {
        ...value,
        startTime: dayjs(value.time[0]).format('YYYY-MM-DD HH:mm:ss'),
        endTime: dayjs(value.time[1]).format('YYYY-MM-DD HH:mm:ss'),
      };
      delete paramData.time;
      console.log('pageChange', paramData);
      getFileList(paramData);
    } else {
      getFileList(value);
    }
  }, [page, size]);

  return (
    <div className="sla-transform-wrap">
      <Form onFinish={onFinish} form={form} autoComplete="off">
        <Row gutter={[16, 16]}>
          <Col span={10}>
            <Form.Item name="time" label="时间范围">
              <RangePicker format="YYYY-MM-DD HH:mm:ss" showTime={{ format: 'HH:mm:ss' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="fileTypes" label="文件类型">
              <Select
                style={{ minWidth: 150 }}
                showArrow
                allowClear
                placeholder="请选择文件类型"
                options={fileType}
                mode="multiple"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="fileVersions" label="文件版本">
              <Select
                style={{ minWidth: 150 }}
                showArrow
                allowClear
                placeholder="请选择文件版本"
                options={fileVersion}
                mode="multiple"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            {/* 对文件名称的模糊搜索 */}
            <Form.Item name="fileName" label="文件名称">
              <Input
                style={{ minWidth: 150 }}
                placeholder="请输入文件名称"
                onChange={handleFileNameInput}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="scenario" label="场景">
              <Select
                style={{ minWidth: 150 }}
                showArrow
                allowClear
                placeholder="请选择场景"
                options={scenarioType}
                // mode="multiple"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="statisticalScope" label="统计指标范围(分钟)">
              <Input style={{ minWidth: 150 }} placeholder="请输入统计指标范围" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="storageFileId" label="storageFileId">
              <Input style={{ minWidth: 150 }} placeholder="请输入storageFileId" allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={17}>
            <div className="transform-wrap">
              <div className="transform-text">
                总数统计，失败：{fileInfo.failedCnt}个，占比：{fileInfo.failedRatio}；成功：
                {fileInfo.successCnt}个
              </div>
              <div className="transform-text">
                GLTF统计，平均耗时：{fileInfo.gltfAvgCost}秒，最短耗时：{fileInfo.gltfMinCost}
                秒，最长耗时：{fileInfo.gltfMaxCost}秒
              </div>
              <div className="transform-text">
                GLB统计，平均耗时：{fileInfo.glbAvgCost}秒，最短耗时：{fileInfo.glbMinCost}
                秒，最长耗时：{fileInfo.glbMaxCost}秒
              </div>
              <div className="transform-text">
                完整转换统计，平均耗时：{fileInfo.totalAvgCost}秒，最短耗时：{fileInfo.totalMinCost}
                秒，最长耗时：{fileInfo.totalMaxCost}秒
              </div>
              {/* <div className="transform-text">成功总数：{fileInfo.successCnt}</div> */}
              {Object.keys(fileInfo.successScopeMap || {})?.map((k: any) => (
                <div className="transform-text">
                  {k}分内, 范围数量：{fileInfo.successScopeMap[k]?.scopeCnt || 0}, 范围占比：
                  {fileInfo.successScopeMap[k]?.scopeRatio || '0%'}
                </div>
              ))}
            </div>
          </Col>
          <Col span={7}>
            <Form.Item>
              <Button type="primary" ghost htmlType="submit" className="btn-search">
                查询
              </Button>
              <Button onClick={downoadAll}>下载</Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div className="transform-list-table">
        <ConfigProvider renderEmpty={renderEmpty}>
          <Table
            columns={columns}
            loading={isLoading}
            dataSource={data}
            scroll={{ x: 1250, y: 'calc(100vh - 430px)' }}
            pagination={{
              size: 'default',
              position: ['bottomRight'],
              showTotal: (total, range) => {
                return `${range[0]}-${range[1]}条，共${total}条`;
              },
              showSizeChanger: true,
              showTitle: true,
              total: total, // 分页总数
              pageSize: size, // 每页数量
              current: page, // 当前页数
              onChange: (p, s) => {
                console.log('翻页', { p, s });
                setPage(p);
                setSize(s as number);
              },
            }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default SlaTransform;
